import { File, RoomMessage, Room } from "../proto/Global_pb";
import { milliSecondsToTimer } from "./utils";
import { protoHandler } from "./proto";
import { CLIENT_SEARCH_ROOM_HISTORY } from "../constants/methods";
import { ClientSearchRoomHistory } from "../proto/ClientSearchRoomHistory_pb";
import store from "../store";
import { setMessengerPlayerAudio } from "../store/messenger/actions";
import { FileGet, idb } from "./fileHandler";
import { FileStatus } from "../constants/globals";
import { FileDownload } from "../proto/FileDownload_pb";
import { downloadURL } from "./file";
import { getStorage, setStorage } from "./storage";
import { STORAGE_AUDIO_ROOM_ID } from "../constants/localstorage";

export default class AudioClass {
  static messageId: RoomMessage.AsObject["messageId"] = "";
  static duration: File.AsObject["duration"];
  static roomId?: Room.AsObject["id"];
  static mainMessageId?: RoomMessage.AsObject["messageId"];

  static get instance(): HTMLAudioElement {
    return document.querySelector("audio#audio_player") as HTMLAudioElement;
  }
  static init() {
    const element = document.createElement("audio");
    element.setAttribute("id", "audio_player");
    element.setAttribute("aria-hidden", "true");

    document.querySelector("body")?.append(element);

    AudioClass.instance.addEventListener("ended", () => {
      AudioClass.next();
    });
  }

  static togglePlaying() {
    if (AudioClass.instance.paused) AudioClass.play();
    else AudioClass.pause();
  }

  static play() {
    AudioClass.refreshAudioMessages("play");
    setTimeout(() => AudioClass.instance.play(), 100);
  }

  static pause() {
    AudioClass.refreshAudioMessages("pause");
    setTimeout(() => AudioClass.instance.pause(), 100);
  }

  static refreshAudioMessages(status?: string) {
    document.querySelectorAll(`div[data-message-id='${AudioClass.messageId}'] i.playing-icon`)?.forEach((element) => {
      if (status === "play") {
        element.classList.remove("icon-play");
        element.classList.add("icon-pause");
      } else {
        element.classList.remove("icon-pause");
        element.classList.add("icon-play");
      }
    });
  }

  static setCurrentTime(time: number) {
    AudioClass.instance.currentTime = time;
  }

  static changeAudio(options: { src: string; messageId?: RoomMessage.AsObject["messageId"]; duration?: File.AsObject["duration"] }) {
    const storeMessageId = store.getState().messenger.playerAudioMessageId.messageId;
    if (AudioClass.instance.src === options.src) return AudioClass.togglePlaying();
    if (AudioClass.messageId !== options.messageId) {
      if (storeMessageId === undefined) {
        AudioClass.instance.src = "";
        AudioClass.pause();
        AudioClass.messageId = "";
        AudioClass.duration = 0;
        return;
      }
      AudioClass.refreshAudioMessages();
    }
    if (!AudioClass.roomId) AudioClass.roomId = store.getState().rooms.currentRoom;
    AudioClass.messageId = options.messageId || "";
    AudioClass.instance.src = options.src;
    AudioClass.duration = options.duration || 0;
    AudioClass.play();
  }

  static download(messageId: RoomMessage.AsObject["messageId"]) {
    AudioClass.pause();
    const attachment = store.getState().messages.messages[`${messageId}`]?.attachment;
    if (attachment) {
      const fileInstance = new FileGet(attachment, FileDownload.Selector.FILE, false);
      return new Promise<string>((resolve) => {
        store.dispatch(setMessengerPlayerAudio({ messageId, status: FileStatus.Downloading }));
        fileInstance?.onFinish((url) => {
          resolve(url);
          AudioClass.changeAudio({ src: url, messageId: messageId, duration: attachment?.duration });
          AudioClass.play();
          store.dispatch(setMessengerPlayerAudio({ messageId, status: FileStatus.DOWNLOADED }));
        });
        fileInstance?.start();
      });
    }
  }

  static onTimeUpdate(func: (e: any) => void) {
    AudioClass.instance.addEventListener("timeupdate", (e: any) => {
      const timerElement = document.querySelector(`div[data-message-id='${AudioClass.messageId}'] span.timer`) as HTMLSpanElement;
      const rangeElement = document.querySelector(`div[data-message-id='${AudioClass.messageId}'] input[type="range"]`) as HTMLInputElement;
      const voiceSeekElement = document.querySelector(`div[data-message-id='${AudioClass.messageId}'] div.voice-seek`) as HTMLDivElement;

      if (timerElement && e.srcElement) {
        if (rangeElement) rangeElement.value = e.srcElement.currentTime || 0;
        if (voiceSeekElement) {
          const seekPercent = (100 * Math.floor(e.srcElement.currentTime)) / AudioClass.duration;
          voiceSeekElement.style.width = `${seekPercent}%`;
        }
        timerElement.innerHTML = `${milliSecondsToTimer(Math.floor(e.srcElement.currentTime))} / ${milliSecondsToTimer(AudioClass.duration)}`;
      }
      func(e);
    });
  }

  static onPlay(func: (e: any) => void) {
    AudioClass.instance.addEventListener("play", func);
  }

  static onPause(func: (e: any) => void) {
    AudioClass.instance.addEventListener("pause", func);
  }

  static loadMore() {
    const roomId = store.getState().rooms?.currentRoom;
    if (!roomId) return;
    protoHandler(CLIENT_SEARCH_ROOM_HISTORY, {
      roomId,
      filter: ClientSearchRoomHistory.Filter.AUDIO,
      offset: store.getState().messages.roomMedia[roomId][ClientSearchRoomHistory.Filter.AUDIO]?.length || 0,
    }).then();
  }
  static checkInCache(messageId: RoomMessage.AsObject["messageId"]) {
    if (messageId === undefined) return;
    AudioClass.refreshAudioMessages();
    AudioClass.messageId = messageId;
    const message = store.getState().messages.messages[`${AudioClass.messageId}`];
    const fileDb = idb.getItem(message.attachment?.token!).then((fileBlob) => {
      if (fileBlob === null) return AudioClass.download(messageId);
      AudioClass.changeAudio({ src: URL.createObjectURL(fileBlob as Blob), messageId: messageId, duration: message.attachment?.duration });
      store.dispatch(setMessengerPlayerAudio({ messageId, status: fileBlob ? FileStatus.DOWNLOADED : FileStatus.Downloading }));
    });
  }

  static next() {
    if (!AudioClass.roomId) return;
    AudioClass.pause();
    const nextMessageIndex = store.getState().messages.roomMedia[AudioClass.roomId][ClientSearchRoomHistory.Filter.AUDIO].indexOf(AudioClass.messageId);
    const nextMessageId = store.getState().messages.roomMedia[AudioClass.roomId][ClientSearchRoomHistory.Filter.AUDIO][nextMessageIndex + 1];
    AudioClass.checkInCache(nextMessageId);
  }

  static prev() {
    if (!AudioClass.roomId) return;
    AudioClass.pause();
    const prevMessageIndex = store.getState().messages.roomMedia[AudioClass.roomId][ClientSearchRoomHistory.Filter.AUDIO].indexOf(AudioClass.messageId);
    const prevMessageId = store.getState().messages.roomMedia[AudioClass.roomId][ClientSearchRoomHistory.Filter.AUDIO][prevMessageIndex - 1];
    AudioClass.checkInCache(prevMessageId);
  }
}
